import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

// Brand-aligned shadcn pattern. The base sets `font-medium` (500) so variants
// don't have to repeat it; `font-semibold` is reserved for genuinely high-
// emphasis CTAs that opt in via className. `gap-2` matches shadcn's icon ↔
// text spacing. Filled variants drop their shadow — the brand-tinted page
// background already gives the button enough contrast, and the shadow was
// reading as visual weight rather than depth.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans font-medium transition-[colors,box-shadow] focus-visible:outline-none focus-visible:shadow-(--shadow-focus-ring-solid) disabled:pointer-events-none disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary:
          'bg-primary-400 text-primary-50 hover:bg-primary-600 active:bg-primary-700 disabled:bg-primary-200 disabled:text-primary-50',
        secondary:
          'bg-surface border border-primary-400 text-primary-400 hover:bg-primary-50 active:bg-primary-100 disabled:bg-primary-50 disabled:border-primary-200 disabled:text-primary-200',
        tertiary:
          'bg-transparent text-primary-400 hover:border-b hover:border-primary-400 active:text-primary-700 active:border-b active:border-primary-700 disabled:text-primary-200',
        ghost: 'bg-transparent text-accent-ink hover:bg-primary-50',
        link: 'text-accent-ink underline-offset-4 hover:underline p-0 h-auto',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm shadow-error-500/15 hover:bg-error-700 active:bg-error-700 focus-visible:shadow-(--shadow-focus-ring-error) disabled:bg-error-300 disabled:shadow-none',
        'outline-destructive':
          'bg-surface border border-error-300 text-error-700 hover:bg-error-50 hover:border-error-500 active:bg-error-50 disabled:bg-neutral-50 disabled:text-neutral-400 disabled:border-(--color-border)',
        outline:
          'bg-surface border border-(--color-border-strong) text-accent-ink hover:bg-neutral-50 hover:border-neutral-300 active:bg-neutral-100 disabled:bg-neutral-50 disabled:text-neutral-400 disabled:border-(--color-border)',
      },
      size: {
        // Compact admin actions — table row CTAs, drawer footers.
        sm: 'h-9 rounded-[8px] px-3 text-sm',
        // Default for admin / workspace UI. Matches shadcn's "default" 40px
        // height and is the right scale next to dense form inputs (also 40px).
        md: 'h-10 rounded-[10px] px-4 text-sm',
        // Hero / dialog primary CTAs that need extra emphasis. Tracks the
        // Input/Select height ramp (44px below lg, 48px from lg up) so auth
        // and onboarding CTAs always sit on the same vertical rhythm as the
        // field stack above them.
        lg: 'h-11 rounded-[10px] px-5 text-sm lg:h-12',
        icon: 'h-10 w-10 rounded-[10px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      // Default is `lg` so auth and onboarding hero buttons (which never pass
      // a size prop) keep their visual emphasis. Admin / workspace screens
      // explicitly opt in to `md` for the denser scale.
      size: 'lg',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const content = asChild ? (
      children
    ) : (
      <>
        {loading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
        {children}
      </>
    );
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={Boolean(disabled) || loading}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
